// src/hooks/useProjects.js
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { useAuthContext } from "../context/AuthContext"; // Import auth context
import {
  getProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
} from "../functions/api_fethcers";

/**
 * Hook for managing projects list and related operations
 *
 * @param {Object} filters - Optional filters for projects list
 * @param {string} filters.clientId - Filter by client ID
 * @param {string} filters.serviceId - Filter by service ID
 * @param {string} filters.status - Filter by status
 * @param {boolean} filters.includeCompleted - Whether to include completed projects
 */
export const useProjects = (filters = {}) => {
  const { isAuthenticated } = useAuthContext(); // Get auth status
  const queryClient = useQueryClient();

  // Define query keys
  const projectsQueryKey = ["projects", filters];

  // Fetch projects list
  const { data, isLoading, isError, error, refetch } = useQuery(
    projectsQueryKey,
    () => getProjects(filters),
    {
      enabled: isAuthenticated(), // Only enable if authenticated
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );

  // Create project mutation
  const createProjectMutation = useMutation(createProject, {
    onSuccess: (newProject) => {
      queryClient.invalidateQueries(projectsQueryKey);
      toast.success("Project created successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create project");
    },
  });

  // Update project mutation
  const updateProjectMutation = useMutation(updateProject, {
    onMutate: async (updatedProject) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries(projectsQueryKey);

      // Snapshot current data
      const previousProjects = queryClient.getQueryData(projectsQueryKey);

      // Optimistically update to the new value
      queryClient.setQueryData(projectsQueryKey, (old) => {
        // If data is not loaded yet, return the old data
        if (!old) return old;

        // Otherwise, update the existing project
        return old.map((project) =>
          project.id === updatedProject.projectId
            ? { ...project, ...updatedProject }
            : project
        );
      });

      return { previousProjects };
    },
    onSuccess: () => {
      queryClient.invalidateQueries(projectsQueryKey);
      toast.success("Project updated successfully");
    },
    onError: (err, _, context) => {
      // Rollback on error
      if (context?.previousProjects) {
        queryClient.setQueryData(projectsQueryKey, context.previousProjects);
      }
      toast.error(err.message || "Failed to update project");
    },
  });

  // Delete project mutation
  const deleteProjectMutation = useMutation(deleteProject, {
    onMutate: async (projectId) => {
      await queryClient.cancelQueries(projectsQueryKey);

      const previousProjects = queryClient.getQueryData(projectsQueryKey);

      // Optimistically remove the project
      queryClient.setQueryData(projectsQueryKey, (old) => {
        // If data is not loaded yet, return the old data
        if (!old) return old;

        // Otherwise, filter out the deleted project
        return old.filter((project) => project.id !== projectId);
      });

      return { previousProjects };
    },
    onSuccess: () => {
      queryClient.invalidateQueries(projectsQueryKey);
      toast.success("Project deleted successfully");
    },
    onError: (err, _, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(projectsQueryKey, context.previousProjects);
      }
      toast.error(err.message || "Failed to delete project");
    },
  });

  // Return data and functions
  return {
    projects: data || [],
    isLoading,
    isError,
    error,
    refetch,

    // Mutations
    createProject: createProjectMutation.mutateAsync,
    updateProject: updateProjectMutation.mutateAsync,
    deleteProject: deleteProjectMutation.mutateAsync,

    // Loading states
    isCreating: createProjectMutation.isLoading,
    isUpdating: updateProjectMutation.isLoading,
    isDeleting: deleteProjectMutation.isLoading,
  };
};

/**
 * Hook for managing a single project and related operations
 *
 * @param {string} projectId - The ID of the project to fetch
 */
export const useProject = (projectId) => {
  const queryClient = useQueryClient();
  const projectQueryKey = ["project", projectId];

  const {
    data: project,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(projectQueryKey, () => getProject(projectId), {
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });

  // Update project mutation
  const updateProjectMutation = useMutation(updateProject, {
    onSuccess: (updatedProject) => {
      queryClient.setQueryData(projectQueryKey, updatedProject);
      queryClient.invalidateQueries(["projects"]);
      toast.success("Project updated successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update project");
    },
  });

  return {
    project,
    isLoading,
    isError,
    error,
    refetch,

    // Mutations
    updateProject: updateProjectMutation.mutateAsync,

    // Loading states
    isUpdating: updateProjectMutation.isLoading,
  };
};

export default useProjects;
